import * as React from 'react';
import styled from 'styled-components';

import {
  useContractContext,
  useFeatures,
  useModalSettingsContext,
  usePostMessageContext,
  usePostMessageHandler,
} from 'src/providers';
import { throttle } from 'src/utils/throttle';

import BannerTestimonial from './Testimonial/Banner';
import { CloseButton } from './CloseButton';
import FAQ from './FAQ';
import Logos from './Logos';
import Main from './Main';
import ReducedDecision from './ReducedDecision';
import { ModalBrandImage } from './ModalBrandImage';
import { useCustomStyleSheet } from 'src/hooks/useCustomStyleSheet';

import './temporary-modal-styles.css';


const ThemedModal = styled.div`
  font-family: var(--font-family);
  color: #000;
  line-height: 1em;
  z-index: 1;
  position: fixed;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  .modal__overlay {
    z-index: 2;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(6, 6, 4, .72);
  }

  .window {
    z-index: 3;
    position: sticky;
    max-height: 96%;
    display: flex;
    flex-direction: row;
    overflow-y: auto;
    overflow-x: hidden;
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0 1px 37px -12px rgba(0, 0, 0, .5);

    // Hide scrollbar
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
    &::-webkit-scrollbar {
      display: none;
    }

    @media (min-height: 720px) {
      min-height: 700px; // At least the height of a typical FAQ page
    }

    @media (max-width: 550px) {
      & {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        height: 100%;
        max-height: 100%;
        width: 100%;
        border: none;
        border-radius: 0;
      }
    }

    .content {
      width: 600px;
      padding: 32px 20px;

      @media (min-width: 551px) {
        & {
          padding: 18px 54px 24px;

          &.reducedDecision {
            padding: 18px 40px 40px 40px;
          }
        }
      }
    }
  }

  // TODO: these should each move downstream to the component that needs it
  .branded-color, .back-button__back-text, .link-2, .contract.selected {
    color: ${props => props.theme.merchantColor} !important;
  }
  .back-button__back-arrow, .faqdropdownarrow {
    fill: ${props => props.theme.merchantColor} !important;
  }
`;

export const Modal = () => {
  const { theme, modalType } = useModalSettingsContext();
  const contractCtx = useContractContext();
  const features = useFeatures();
  const { postMessage } = usePostMessageContext();
  const [activeTab, setActiveTab] = React.useState('main');
  const [modalEl, setModalEl] = React.useState<HTMLDivElement | null>(null);
  const [loaded, setLoaded] = React.useState(false);

  useCustomStyleSheet();

  const modalStyle = React.useMemo(() => {
    return features.reducedDecisionModal ? 'reducedDecision' : 'default';
  }, [features.reducedDecisionModal]);

  // Reset to main tab when the flow is resolved (protection added or declined)
  React.useEffect(() => {
    if (contractCtx.resolved) {
      setActiveTab('main');
    }
  }, [contractCtx.resolved]);

  const setModalRef = React.useCallback((el: HTMLDivElement) => {
    setModalEl(el);
  }, [setModalEl]);

  const modalResizeHandler = () => {
    if (modalEl && loaded) {
      modalEl.style.minHeight = '';
      modalEl.style.minHeight = `${modalEl.offsetHeight}px`;
    }
  };

  const updateModalHeight = React.useCallback(
    throttle(modalResizeHandler, 50),
    [modalEl, loaded]
  );

  React.useEffect(() => {
    updateModalHeight();
  }, [updateModalHeight]);

  React.useEffect(() => {
    window.addEventListener('resize', updateModalHeight);
    return () => {
      window.removeEventListener('resize', updateModalHeight);
    };
  }, [modalEl, loaded]);

  const closeButtonRef: React.RefObject<HTMLButtonElement> = React.useRef(null);

  const faqClickHandler = React.useCallback(() => {
    postMessage('viewFaq', { ctaType: 'modal', modalType });
    setActiveTab('faq');
  }, [postMessage, setActiveTab, modalType]);

  const faqExpandHandler = React.useCallback((question: string, index: string) => {
    postMessage('faqQueryOpened', { ctaType: 'modal', modalType, question, index });
  }, [postMessage, modalType]);

  const renderTab = () => {
    switch (activeTab) {
      case 'faq':
        return <FAQ onBackRequested={() => { setActiveTab('main'); }} onFaqExpanded={faqExpandHandler} />;
      case 'main':
      default:
        return features.reducedDecisionModal ?
          <ReducedDecision closeButtonRef={closeButtonRef} onFAQClicked={faqClickHandler} /> :
          <Main closeButtonRef={closeButtonRef} onFAQClicked={faqClickHandler} />;
    }
  };

  usePostMessageHandler('setFocusTrap', React.useCallback(() => {
    if (activeTab === 'main') {
      closeButtonRef?.current?.focus();
    }
  }, [activeTab]));

  const handleOverlayClick = (event: React.MouseEvent) => {
    const target = event.target as Element;
    if (target.classList.contains('modal__overlay')) {
      contractCtx.declineProtection('overlay-click', modalType);
    }
  };

  const getFocusableElements = () => {
    if (!modalEl) {
      return [];
    }
    const focusableChildren = modalEl.querySelectorAll('a, button, [tabindex="0"]');
    return Array.prototype.slice.call(focusableChildren);
  };

  React.useEffect(() => {
    const focusableElements = getFocusableElements();
    if (focusableElements.length) {
      focusableElements[0].focus();
    }
  }, []);

  const manageFocus = React.useCallback((e: React.KeyboardEvent) => {
    if (e.keyCode === 27) { // Escape
      contractCtx.declineProtection('esc_key', modalType);
    }

    if (!modalEl || e.key !== 'Tab') return;

    const focusableElements = getFocusableElements();
    if (!focusableElements.length) {
      return;
    }

    const firstTabStop = focusableElements[0];
    const lastTabStop = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === focusableElements[0]) {
        e.preventDefault();
        lastTabStop.focus();
      }
    } else {
      if (document.activeElement === lastTabStop) {
        e.preventDefault();
        firstTabStop.focus();
      }
    }
  }, [modalEl, contractCtx.declineProtection]);

  const maybeRenderTestimonial = () => {
    if (features.showTestimonial) {
      return <BannerTestimonial />;
    }

    return null;
  };

  const maybeRenderModalBrandImage = () => {
    if (!features.showModalBrandImage) {
      return null;
    }

    return <ModalBrandImage />;
  };

  if (!contractCtx.contracts.length) {
    return null;
  }

  return (
    <ThemedModal
      aria-label="Protect your purchase"
      aria-modal="true"
      className="modal clyde-modal"
      role="dialog"
      tabIndex={-1}
      onKeyDown={manageFocus}
    >
      <div
        aria-hidden="true"
        className="modal__overlay"
        onClick={handleOverlayClick}
      />

      <div className="window" ref={setModalRef}>
        <div className={`content ${modalStyle}`}>
          {maybeRenderTestimonial()}
          <CloseButton ref={closeButtonRef} />
          <Logos
            onLoad={() => setLoaded(true)}
            partnerLogo={theme.partnerLogo}
            shopName={theme.shopName}
            showXCoverBranding={theme.showXCoverBranding}
          />
          {renderTab()}
        </div>
        {maybeRenderModalBrandImage()}
      </div>
    </ThemedModal>
  );
};
